import { useEffect, useState } from 'react';
import { BRAND_LOGO_ROWS } from '@/assets/brand-logos';
import usersIcon from '../../assets/users-icon.svg';
import { ProgressIndicators } from '../progress-indicators';
import { OnboardingContinueButton } from '../onboarding-continue-button';
import { OnboardingScreen, ScaleToFit } from '../onboarding-layout';

const START_CLAIMS = 132_482;
const TICK_MS = 1100;
const MIN_BUMP = 1;
const MAX_BUMP = 17;
const MARQUEE_HEIGHT = 256;

interface Step6Props {
  onContinue: () => void;
}

function formatClaims(value: number): string {
  return `$${Math.round(value).toLocaleString('en-US')}`;
}

function LogoMarqueeRow({
  logos,
  direction,
}: {
  logos: readonly string[];
  direction: 'left' | 'right';
}) {
  const track = [...logos, ...logos];

  return (
    <div className="w-full overflow-hidden">
      <div
        className={
          direction === 'right'
            ? 'flex w-max animate-[onboarding-logo-marquee-right_40s_linear_infinite] gap-3'
            : 'flex w-max animate-[onboarding-logo-marquee-left_40s_linear_infinite] gap-3'
        }
      >
        {track.map((src, index) => (
          <img
            key={`${src}-${index}`}
            src={src}
            alt=""
            className="size-[72px] shrink-0 rounded-[18px] object-cover"
          />
        ))}
      </div>
    </div>
  );
}

export function OnboardingStep6({ onContinue }: Step6Props) {
  const [claims, setClaims] = useState(START_CLAIMS);

  useEffect(() => {
    const id = window.setInterval(() => {
      const bump = MIN_BUMP + Math.floor(Math.random() * (MAX_BUMP - MIN_BUMP + 1));
      setClaims((prev) => prev + bump);
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  return (
    <OnboardingScreen
      slot="onboarding-step-6"
      header={
        <div className="flex w-full flex-col items-center gap-3">
          <ProgressIndicators currentStep={5} />

          <div className="w-full text-center">
            <h1 className="font-display text-[28px] font-bold leading-9 text-mf-neutral-900">
              Let&apos;s find out what settlements you might have missed.
            </h1>
            <p className="mt-2 text-base leading-5 text-mf-neutral-600">
              We&apos;ll ask about the products you&apos;ve used and check them against active
              settlements.
            </p>
          </div>
        </div>
      }
      footer={
        <div className="flex w-full flex-col items-center gap-6">
          <p className="flex items-center gap-1.5 text-sm leading-4 text-mf-neutral-400">
            <img src={usersIcon} alt="" className="size-5 shrink-0" />
            <span>
              Users filed{' '}
              <span className="font-bold text-mf-green">{formatClaims(claims)}</span> in claims
              today
            </span>
          </p>

          <OnboardingContinueButton onClick={onContinue} label="Get started" />
        </div>
      }
    >
      <ScaleToFit naturalHeight={MARQUEE_HEIGHT} lockWidth>
        <div className="flex h-full w-full flex-col justify-center gap-3 overflow-hidden py-2">
          <LogoMarqueeRow logos={BRAND_LOGO_ROWS[0]} direction="right" />
          <LogoMarqueeRow logos={BRAND_LOGO_ROWS[1]} direction="left" />
          <LogoMarqueeRow logos={BRAND_LOGO_ROWS[2]} direction="right" />
        </div>
      </ScaleToFit>
    </OnboardingScreen>
  );
}
